import { graphql } from 'react-relay';
import { useEffect } from 'react';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useIsGuest } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useShareableLinkContentQueryShareableLinkContentQuery$data as ShareableLinkContentQueryData$data,
	useShareableLinkContentQueryShareableLinkContentQuery$variables as ShareableLinkContentQueryData$variables,
} from './__generated__/useShareableLinkContentQueryShareableLinkContentQuery.graphql.ts';

export type ShareableLinkContentData = {
	contentType: string;
	canEditShareableLink: boolean;
};

export type ShareableLinkContentQueryData = {
	isLoading: boolean;
	error?: Error;
	data?: ShareableLinkContentData;
};

/**
 * Computes content type and whether the current user can edit shareable link settings.
 *
 * Derives `canEditShareableLink` from:
 * - User has update permission for the content type, OR is a space admin
 * - AND is not a guest (external collaborator)
 */
const computeContentPermissions = ({
	data,
	isGuest,
}: {
	data: ShareableLinkContentQueryData$data | undefined;
	isGuest: boolean;
}): ShareableLinkContentData => {
	const contentType = data?.content?.nodes?.[0]?.type || '';
	const operations = data?.content?.nodes?.[0]?.operations || [];
	const isSpaceAdmin = Boolean(data?.space?.currentUser?.isAdmin);
	const canEditPage = operations.some(
		(operationCheckResult) =>
			operationCheckResult?.operation === 'update' &&
			operationCheckResult?.targetType === contentType,
	);

	return {
		contentType,
		canEditShareableLink: (isSpaceAdmin || canEditPage) && !isGuest,
	};
};

/**
 * Fetches content type and user edit permissions for a given content ID.
 *
 * Queries the content's type (page, whiteboard) and whether the current user has
 * update permissions, accounting for space admin status and guest access.
 */
export const useShareableLinkContentQuery = (props: {
	contentId: string;
	skip?: boolean;
}): ShareableLinkContentQueryData => {
	const spaceKey = useSpaceKey();
	const isGuest = useIsGuest();
	const { loading, data, error } = useQueryInterop<
		ShareableLinkContentQueryData$data,
		ShareableLinkContentQueryData$variables
	>(
		graphql`
			query useShareableLinkContentQueryShareableLinkContentQuery(
				$contentId: ID!
				$spaceKey: String!
				$hasSpaceKey: Boolean!
			) {
				content(id: $contentId) {
					nodes {
						id
						type
						operations {
							operation
							targetType
						}
					}
				}
				space(key: $spaceKey) @include(if: $hasSpaceKey) {
					id
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			skip: props.skip,
			variables: {
				contentId: props.contentId,
				hasSpaceKey: Boolean(spaceKey),
				spaceKey: spaceKey ?? '',
			},
		},
	);
	useEffect(() => {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
	}, [error]);

	return loading
		? { isLoading: true }
		: {
				isLoading: false,
				error,
				data: error ? undefined : computeContentPermissions({ data, isGuest }),
			};
};
